import { LogOut, Monitor, Moon, ShieldCheck, Sun, History } from 'lucide-react';
import { useTheme } from '@/lib/theme';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { ROLE_LABELS } from '@portal/contracts';
import { post, setCsrfToken } from '@/lib/api';
import { useBusiness } from '@/lib/business';
import { SESSION_KEY, useSession } from '@/lib/session';

const initials = (name: string) => name.split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('');

export function UserMenu() {
  const { session } = useSession();
  const { role, can } = useBusiness();
  const { theme, setTheme } = useTheme();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const logout = useMutation({
    mutationFn: () => post('/auth/logout', undefined, { business: false }),
    onSettled: () => {
      setCsrfToken(null);
      // Not qc.clear(): that detaches the session observer, which then keeps showing the old user and bounces /login back to /.
      qc.setQueryData(SESSION_KEY, null);
      qc.removeQueries({ predicate: (query) => query.queryKey[0] !== SESSION_KEY[0] });
      navigate('/login', { replace: true });
    },
  });
  if (!session) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="sm" className="gap-2 px-1.5">
          <Avatar className="size-6">
            <AvatarFallback aria-hidden className="bg-secondary text-[11px] font-medium text-foreground">{initials(session.user.fullName)}</AvatarFallback>
          </Avatar>
          <span className="hidden max-w-32 truncate text-sm lg:inline">{session.user.fullName}</span>
          <span className="sr-only">Account menu</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64">
        <DropdownMenuLabel>
          <div className="truncate text-sm font-semibold">{session.user.fullName}</div>
          <div className="truncate text-xs font-normal text-muted-foreground">{session.user.email}</div>
          {role && <div className="mt-1 text-xs font-normal text-muted-foreground">{ROLE_LABELS[role]}</div>}
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link to="/settings/security">
            <ShieldCheck /> Security & sessions
          </Link>
        </DropdownMenuItem>
        {can('audit:view') && (
          <DropdownMenuItem asChild>
            <Link to="/settings/audit">
              <History /> Activity log
            </Link>
          </DropdownMenuItem>
        )}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs text-muted-foreground">Theme</DropdownMenuLabel>
        <DropdownMenuRadioGroup value={theme ?? 'system'} onValueChange={setTheme}>
          <DropdownMenuRadioItem value="light">
            <Sun className="size-4" /> Light
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="dark">
            <Moon className="size-4" /> Dark
          </DropdownMenuRadioItem>
          <DropdownMenuRadioItem value="system">
            <Monitor className="size-4" /> System
          </DropdownMenuRadioItem>
        </DropdownMenuRadioGroup>
        <DropdownMenuSeparator />
        <DropdownMenuItem onSelect={() => logout.mutate()}>
          <LogOut /> Sign out
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
